// Composer Studio — 반복영역 "셀 지정" 픽(cellPick pickKind='cell') 중, 영역의 행 밴드
// (rowStart~rowEnd)를 캔버스에 강조하고 그 외 행은 dim 처리한다(매핑 가능 행 가이드).
// 다행 반복(항목당 2행+)에서는 같은 소스의 필드가 밴드 안 서로 다른 행에 앉으므로
// 단일 anchor 행이 아니라 **밴드**가 허용 범위다(2026-08-28 — 종전 단일 행 제약은
// 2행 배치 저작을 막던 결함). anchorRows가 없으면 강조하지 않는다 — best-effort.

import { getNodeRect, rectRelativeTo } from './canvasRect';

/** 행 밴드 허용 판정 — 픽 거부 게이트와 강조가 같은 규칙을 쓴다(spec의 되돌림 대상). */
export function rowWithinAnchorRows(
  row: number,
  anchorRows: { start: number; end: number } | null | undefined,
): boolean {
  if (anchorRows == null) return true;
  return row >= anchorRows.start && row <= anchorRows.end;
}

/**
 * data-composer-cell="tableId::row::col" 요소들 중 (tableId, anchorRows 밴드) 행들에 속한
 * 셀들의 세로 밴드(top~bottom)를 canvas 상대좌표로 구한다. 못 찾으면 null.
 */
function anchorRowBand(
  canvas: HTMLElement,
  tableId: string,
  anchorRows: { start: number; end: number },
): { top: number; height: number } | null {
  const cells = canvas.querySelectorAll<HTMLElement>('[data-composer-cell]');
  let top = Infinity;
  let bottom = -Infinity;
  cells.forEach((el) => {
    const ds = el.dataset.composerCell ?? '';
    const parts = ds.split('::');
    if (parts[0] === tableId && rowWithinAnchorRows(Number(parts[1]), anchorRows)) {
      const r = rectRelativeTo(canvas, el);
      top = Math.min(top, r.top);
      bottom = Math.max(bottom, r.top + r.height);
    }
  });
  if (top === Infinity) return null;
  return { top, height: bottom - top };
}

const DIM_BG = 'rgba(17, 24, 39, 0.18)';

/**
 * tableId·anchorRows가 모두 주어졌을 때만 표시. 표 전체에서 영역 행 밴드를 파랑 강조하고,
 * 그 위/아래(같은 표 범위)를 반투명 dim으로 덮는다(다른 행은 매핑 불가임을 시각화). pointerEvents 없음(클릭 통과).
 */
export function RepeatRowHighlightOverlay({
  canvas,
  tableId,
  anchorRows,
}: {
  canvas: HTMLElement | null;
  tableId: string | null | undefined;
  anchorRows: { start: number; end: number } | null | undefined;
}) {
  if (!canvas || !tableId || anchorRows == null) return null;
  const tableRect = getNodeRect(canvas, tableId);
  if (!tableRect) return null;
  const band = anchorRowBand(canvas, tableId, anchorRows);
  if (!band) return null;

  const aboveHeight = Math.max(0, band.top - tableRect.top);
  const belowTop = band.top + band.height;
  const belowHeight = Math.max(0, tableRect.top + tableRect.height - belowTop);

  return (
    <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none', zIndex: 38 }}>
      {/* anchor 행 위 dim */}
      {aboveHeight > 0 && (
        <div
          style={{
            position: 'absolute',
            top: tableRect.top,
            left: tableRect.left,
            width: tableRect.width,
            height: aboveHeight,
            background: DIM_BG,
          }}
        />
      )}
      {/* anchor 행 아래 dim */}
      {belowHeight > 0 && (
        <div
          style={{
            position: 'absolute',
            top: belowTop,
            left: tableRect.left,
            width: tableRect.width,
            height: belowHeight,
            background: DIM_BG,
          }}
        />
      )}
      {/* anchor 행 강조 */}
      <div
        style={{
          position: 'absolute',
          top: band.top,
          left: tableRect.left,
          width: tableRect.width,
          height: band.height,
          outline: '2px solid #4f46e5', // brand-600
          outlineOffset: '-1px',
          background: 'rgba(37, 99, 235, 0.10)',
        }}
      />
    </div>
  );
}
